import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateTransferRequest, Transfer, TransferQuery } from '@financial/shared';
import { api } from '../../lib/api';
import { notify } from '../../store/notifications';

function keyFor(query: TransferQuery) {
  return ['transfers', query];
}

async function listTransfers(query: TransferQuery): Promise<Transfer[]> {
  const res = await api.get<Transfer[]>('/transfers', { params: query });
  return res.data;
}

export function useTransfers(query: TransferQuery = {}) {
  return useQuery({ queryKey: keyFor(query), queryFn: () => listTransfers(query) });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['transfers'] });
    void qc.invalidateQueries({ queryKey: ['accounts'] });
    void qc.invalidateQueries({ queryKey: ['net-worth'] });
    void qc.invalidateQueries({ queryKey: ['budget'] });
    void qc.invalidateQueries({ queryKey: ['insights'] });
  };
}

export function useCreateTransfer() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: CreateTransferRequest) => {
      const res = await api.post<Transfer>('/transfers', body);
      return res.data;
    },
    onSuccess: (transfer, variables) => {
      invalidate();
      if (variables.reconciliation) {
        notify.success('Traspaso registrado y saldo conciliado');
        return;
      }
      notify.success('Traspaso registrado', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await api.delete(`/transfers/${transfer.id}`);
            invalidate();
            notify.info('Traspaso deshecho');
          },
        },
      });
    },
    onError: (_error, variables) => {
      if (variables.reconciliation) invalidate();
    },
  });
}

export function useDeleteTransfer() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/transfers/${id}`);
    },
    onSuccess: invalidate,
  });
}
